import { AVAILABLE_COUNTRIES_REGISTRATION } from '@constants';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Box, Button, IconButton, Link, Stack, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import i18n from '~/lang';
import useTheme from '~/theme/ThemeProvider';
import { getCurrentCountry } from '~/utils/countryUtils';

import Dialog from './Dialog';

export default function SupportDialog() {
  const appTheme = useTheme();
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(() => getCurrentCountry().value);

  const handleClose = () => setOpen(false);

  const currentLanguage = i18n.resolvedLanguage as keyof (typeof AVAILABLE_COUNTRIES_REGISTRATION)[number]['name'];

  return (
    <>
      <Button
        disableRipple
        variant="text"
        onClick={() => setOpen(true)}
        sx={{
          height: 24,
          color: 'inherit',
          '&:focus-visible': {
            outline: '2px solid currentColor',
            outlineOffset: '2px',
          },
          textDecoration: 'underline',
          textUnderlineOffset: 4,
          textDecorationThickness: 1,
        }}
      >
        <Typography component={Link}>
          <Trans i18nKey="footer.support" />
        </Typography>
      </Button>
      <Dialog
        open={open}
        handleClose={handleClose}
        showCloseIcon={false}
        maxWidth="sm"
        title={<Trans i18nKey="footer.support" />}
        body={
          <>
            <Tabs value={currentTab} onChange={(event, value) => setCurrentTab(value)}>
              {AVAILABLE_COUNTRIES_REGISTRATION.map(country => (
                <Tab key={country.value} label={country.name[currentLanguage]} value={country.value} />
              ))}
            </Tabs>
            {AVAILABLE_COUNTRIES_REGISTRATION.map(country => {
              if (country.value !== currentTab) return null;
              return (
                <Stack key={country.value} sx={{ width: '100%', alignItems: 'normal', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: appTheme.colors.PRIMARY_LIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <SupportAgentIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography variant="body1">
                      <Link href={`tel:${country.support.phone}`} color="text.primary">
                        {country.support.phone}
                      </Link>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: appTheme.colors.PRIMARY_LIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <MailOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography variant="body1">
                      <Link href={`mailto:${country.support.email}`} underline="always" color="text.primary">
                        {country.support.email}
                      </Link>
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={t('accessibility.copyEmail')}
                      title={t('accessibility.copyEmail')}
                      onClick={() => {
                        navigator.clipboard?.writeText(country.support.email).catch(() => {
                          // clipboard write failed silently
                        });
                      }}
                      sx={{
                        width: 'unset',
                        border: 'none',
                        p: 0.5,
                        height: 'fit-content',
                        '&:focus-visible': {
                          outline: `2px solid`,
                          outlineOffset: '2px',
                        },
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Stack>
              );
            })}
          </>
        }
      />
    </>
  );
}
